import React, { useMemo, useState } from 'react';
import { Dropdown, DatePicker, message } from '@maxtropy/components';
import dayjs from 'dayjs';
import { TrendGranularity } from '@/pages/EnvMonitorKanban/types';
import { GRANULARITY_OPTIONS, GasArray, WaterArray } from '@/pages/EnvMonitorKanban/constants';
import { getMaxRange, getDefaultDateRange } from '@/pages/EnvMonitorKanban/utils';
import MonitorBlock from '../MonitorBlock';
import styles from './index.module.scss';

const RightPanel: React.FC = () => {
  const [gasPropertyId, setGasPropertyId] = useState<number>(GasArray[0].propertyId);
  const [waterPropertyId, setWaterPropertyId] = useState<number>(WaterArray[0].propertyId);
  const [granularity, setGranularity] = useState<TrendGranularity>(TrendGranularity.DAY);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(() =>
    getDefaultDateRange(TrendGranularity.DAY)
  );

  const dropdownDisplayText = useMemo(() => {
    if (!dateRange?.[0] || !dateRange?.[1]) return '请选择时间';
    const format = granularity === TrendGranularity.MONTH ? 'YYYY-MM' : 'YYYY-MM-DD';
    return `${dateRange[0].format(format)} ~ ${dateRange[1].format(format)}`;
  }, [dateRange, granularity]);

  const handleGranularityChange = (g: TrendGranularity) => {
    setGranularity(g);
    setDateRange(getDefaultDateRange(g));
  };

  const handleDateRangeChange = (dates: [dayjs.Dayjs, dayjs.Dayjs] | null) => {
    if (!dates?.[0] || !dates?.[1]) {
      setDateRange(dates);
      return;
    }
    const maxRange = getMaxRange(granularity);
    const diff =
      granularity === TrendGranularity.MONTH ? dates[1].diff(dates[0], 'month') : dates[1].diff(dates[0], 'day');
    if (Math.abs(diff) > maxRange) {
      message.error({
        content: `最多选择 ${maxRange + 1} ${granularity === TrendGranularity.MONTH ? '个月' : '天'}`,
        key: 'right-panel-date-range-warning',
      });
      return;
    }
    setDateRange(dates);
  };

  const datePickerFormat = granularity === TrendGranularity.MONTH ? 'YYYY-MM' : 'YYYY-MM-DD';

  const timeControlsDropdown = (
    <Dropdown
      type="link"
      className={styles.dropdownWrapper}
      getPopupContainer={() => document.getElementById('full_screen_box') ?? document.body}
      trigger={['click']}
      dropdownRender={() => (
        <div className={styles.dropdownMenuWrapper}>
          <div className={styles.timeControlsContent}>
            <div className={styles.granularityBtns}>
              {GRANULARITY_OPTIONS.map(opt => (
                <div
                  key={opt.value}
                  className={`${styles.dateBtn} ${granularity === opt.value ? styles.active : ''}`}
                  onClick={() => handleGranularityChange(opt.value)}
                >
                  {opt.label}
                </div>
              ))}
            </div>
            <DatePicker.RangePicker
              getPopupContainer={() => document.getElementById('full_screen_box') ?? document.body}
              value={dateRange}
              onChange={dates => handleDateRangeChange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
              picker={granularity === TrendGranularity.MONTH ? 'month' : 'date'}
              format={datePickerFormat}
              allowClear={false}
            />
          </div>
        </div>
      )}
    >
      <span className={styles.dropdownText}>{dropdownDisplayText}</span>
    </Dropdown>
  );

  return (
    <div className={styles.rightPanel}>
      <MonitorBlock
        title="整体VOC废气监测"
        items={GasArray}
        activeId={gasPropertyId}
        color="#00ADFF"
        deviceType={1}
        chartHeight={270}
        granularity={granularity}
        dateRange={dateRange}
        extra={timeControlsDropdown}
        onChange={setGasPropertyId}
      />
      <MonitorBlock
        title="整体废水监测"
        items={WaterArray}
        activeId={waterPropertyId}
        color="#16DD8E"
        chartHeight={270}
        deviceType={2}
        granularity={granularity}
        dateRange={dateRange}
        onChange={setWaterPropertyId}
      />
    </div>
  );
};

export default RightPanel;
